import { useCallback, useEffect, useState } from "react";

/**
 * The client side of a "send a new code" budget: how many sends have happened, whether another is
 * allowed, and a cooldown that ticks down once a second after a resend. The server keeps the real
 * budget; this only keeps the button honest between round trips. Shared by the enrollment wizard's
 * email step and the confirmation dialog, so the counting exists once.
 */
export const useResendBudget = (options: { maxSends?: number; cooldownMs?: number } = {}) => {
  const { maxSends = 3, cooldownMs = 30_000 } = options;
  const [sends, setSends] = useState(0);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [now, setNow] = useState(() => Date.now());

  // Ticks the cooldown down once a second while it is running.
  useEffect(() => {
    if (cooldownUntil <= now) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [cooldownUntil, now]);

  const recordSend = useCallback(() => setSends((count) => count + 1), []);
  // Starts the instant it is called, not once a request comes back, so a second click cannot slip
  // in while the first resend is still in flight.
  const startCooldown = useCallback(() => {
    const until = Date.now() + cooldownMs;
    setCooldownUntil(until);
    setNow(Date.now());
  }, [cooldownMs]);
  const reset = useCallback(() => {
    setSends(0);
    setCooldownUntil(0);
  }, []);

  const cooldownLeft = Math.max(0, Math.ceil((cooldownUntil - now) / 1000));
  return { sends, canResend: sends < maxSends, cooldownLeft, recordSend, startCooldown, reset };
};
